/**
 * Aggregation Enrollment Page
 * Route: /aggregation-enrollment
 * Allows SMEs to express interest in the Aggregated Carbon Program.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useJourney } from "@/context/JourneyContext";
import client from "@/api/client";
import { GitBranch, CheckCircle2, ArrowLeft, Shield, Layers, Users, TrendingDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const BENEFITS = [
  { icon: Layers,      title: "Pool your reductions",    desc: "Multiple SMEs aggregate emissions reductions into a viable carbon project — overcoming the scale barrier." },
  { icon: Users,       title: "Shared MRV costs",        desc: "Monitoring, Reporting and Verification costs are distributed across participants, making it affordable." },
  { icon: TrendingDown,title: "Access voluntary markets",desc: "Aggregated projects can be registered under Gold Standard or Verra VCS — unlocking real carbon revenue." },
  { icon: Shield,      title: "DeCarbonIQ managed",      desc: "Our team handles project structure, methodology selection, and buyer connections on your behalf." },
];

export default function AggregationEnrollment() {
  const navigate = useNavigate();
  const { journeyData } = useJourney();
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const org  = journeyData?.organization;
  const fp   = journeyData?.footprint;
  const crs  = journeyData?.cars_score;
  const opp  = journeyData?.opportunity;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await client.post("/sme/aggregation-interest", { note });
      setSubmitted(true);
      toast.success("Interest registered. Our team will contact you within 48 hours.");
    } catch {
      toast.error("Could not submit. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="p-6 lg:p-8 min-h-screen">
      {/* Back nav */}
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft size={13} /> Back to Finance Brief
      </button>

      {/* Header */}
      <div className="mb-7">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center">
            <GitBranch size={18} className="text-teal-600" />
          </div>
          <div>
            
      <PageHeader
        label="DeCarbonIQ Program"
        title="Aggregated Carbon Program"
      />

      <MetricCard label="Enrollment Steps" value={steps.length} color="text-slate-900" />
        </div>
      </div>
      <p className="text-sm text-slate-500 leading-relaxed mb-7">
        Join a pooled portfolio of verified SME carbon reductions — structured for voluntary carbon market participation.
        No individual MRV burden. No minimum size requirement.
      </p>
      </div>

      {/* Your Readiness */}
      {fp?.total_tco2e && (
        <SectionCard>

          <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Your Current Readiness</p>
          <div className="grid grid-cols-3 gap-4">
            {[
              ["Emissions",    `${fp.total_tco2e} tCO₂e/yr`, "text-slate-900"],
              ["CRS Score",   `${Math.round(crs?.score || 0)}/100`, "text-teal-700"],
              ["Reduction",   `${opp?.total_opportunity_tco2e || 0}t/yr potential`, "text-emerald-700"],
            ].map(([l, v, c]) => (
              <div key={l} className="bg-slate-50 rounded-xl px-3 py-2.5 text-center">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">{l}</p>
                <p className={`text-sm font-bold ${c}`}>{v}</p>
              </div>
            ))}
          </div>
        
        </SectionCard>
      )}

      {/* How it works */}
      <SectionCard>

        <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-4">How It Works</p>
        <div className="space-y-4">
          {BENEFITS.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-3">
              <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon size={14} className="text-teal-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-slate-900">{title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-snug">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      
      </SectionCard>

      {/* Express Interest Form */}
      {!submitted ? (
        <div className="bg-white border-2 border-teal-200 rounded-xl p-6">
          <p className="text-sm font-bold text-slate-900 mb-1">Express Your Interest</p>
          <p className="text-xs text-slate-500 mb-4">
            No commitment required. Our team will review your profile and contact you within 48 hours.
          </p>
          {org && (
            <div className="bg-slate-50 rounded-xl px-4 py-3 mb-4">
              <p className="text-xs text-slate-400 mb-0.5">Registering as</p>
              <p className="text-sm font-bold text-slate-900">{org.name}</p>
              <p className="text-xs text-slate-500">{org.sector} · {org.country}</p>
            </div>
          )}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Any specific goals or questions? <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="e.g. We want to reduce our fleet emissions and understand our carbon credit potential..."
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:border-teal-400 bg-white"
              data-testid="aggregation-note-input"
            />
          </div>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50 transition-colors"
            data-testid="aggregation-submit-btn">
            {loading
              ? <><RefreshCw size={14} className="animate-spin" /> Submitting...</>
              : <><GitBranch size={14} /> Register Interest</>
            }
          </button>
          <p className="text-xs text-slate-400 text-center mt-2">
            No commitment. Our team reviews all applications before onboarding.
          </p>
        </div>
      ) : (
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-6 text-center" data-testid="aggregation-success">
          <CheckCircle2 size={32} className="text-emerald-600 mx-auto mb-3" />
          <p className="text-base font-bold text-emerald-800 mb-1">Interest Registered</p>
          <p className="text-sm text-emerald-600 mb-4">
            Thank you. Our carbon program team will contact you within 48 hours.
          </p>
          <div className="bg-white rounded-xl px-4 py-3 text-left space-y-2 mb-5">
            {[
              "Portfolio eligibility review (3–5 business days)",
              "Introductory call with carbon program coordinator",
              "MRV framework briefing for eligible projects",
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-4 h-4 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold text-teal-700">{i+1}</div>
                <p className="text-xs text-slate-600">{step}</p>
              </div>
            ))}
          </div>
          <button onClick={() => navigate("/sme/journey")}
            className="px-6 py-2.5 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800">
            Back to Journey
          </button>
        </div>
      )}
    </div>
  );
}
